import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Sidebar from "../../../components/Sidebar";
import DataTable from "../../../components/DataTable";
import { fetchAgentClients } from "../../../controller/ibDashboard/clientsController";
import { extractEmailLoose } from "../../../utils/extractEmailLoose";
import { useMediaMaxWidth } from "../../../hooks/useMediaMaxWidth";
import {
  getPermissionControllers,
  hasBrokerPermission,
  isAccountPermissionDisabled,
  isBroker,
} from "../../../utils/permissionHelper";
import PermissionBrokerModelController from "../../../controller/model/PermissionBrokerModelController";
import { fetchClientsTradingHistory, fetchClientsAccountTotals } from "../../../controller/ibDashboard/tradingHistoryController";
import DateRangeFilter from "../../../components/DateRangeFilter";
import AppConstants from "../../../utils/appConstants";
import "../../css/List.css";
import "../../css/IbDashboard.css";
import "../../css/TransactionsHistory.css";

const PER_PAGE = 8;

const NAN = "NAN";

const formatLots = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00";
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatMoney = (value) => {
  const n = Number(value);
  const safe = Number.isFinite(n) ? n : 0;
  return `$${safe.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const isMissing = (v) =>
  v === null ||
  v === undefined ||
  (typeof v === "string" && String(v).trim() === "") ||
  (typeof v === "number" && Number.isNaN(v));

const disp = (v) => (isMissing(v) ? NAN : typeof v === "string" ? v.trim() : String(v));

const firstDefined = (...vals) => {
  for (const i of vals) {
    if (!isMissing(i)) return i;
  }
  return null;
};

const getUserInfo = (c) => c?.user_information || c?.userInformation || {};

const avatarInitials = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  return (parts[0]?.slice(0, 2) || "?").toUpperCase();
};

function parseClientStatus(client, info) {
  const profileStatus = firstDefined(
    client.PROFILE_STATUS,
    info.PROFILE_STATUS,
    client.profile_status,
    info.profile_status
  );
  if (!isMissing(profileStatus)) {
    const n = typeof profileStatus === "string" ? Number(profileStatus.trim()) : Number(profileStatus);
    if (!Number.isNaN(n)) {
      if (n === 1) return "completed";
      if (n === 0) return "incomplete";
    }
  }

  const s = firstDefined(
    client.USER_STATUS,
    info.USER_STATUS,
    client.STATUS,
    info.STATUS,
    client.IS_ACTIVE,
    info.IS_ACTIVE,
    client.user_status,
    client.ACTIVE
  );
  if (isMissing(s)) return null;
  if (typeof s === "number") {
    if (s === 1) return "active";
    if (s === 0) return "inactive";
  }
  const str = String(s).toLowerCase().trim();
  if (["1", "true", "active", "a", "enabled"].includes(str)) return "active";
  if (["0", "false", "inactive", "i", "disabled"].includes(str)) return "inactive";
  if (str.includes("suspend")) return "suspended";
  if (str.includes("pending") || str === "2") return "pending";
  return str || null;
}

function parseClientKyc(client, info) {
  const k = firstDefined(
    client.IS_KYC_VERIFIED,
    info.IS_KYC_VERIFIED,
    client.KYC,
    info.KYC,
    client.KYC_STATUS,
    info.KYC_STATUS,
    client.kyc_status,
    info.kyc_status
  );
  if (isMissing(k)) return null;
  const n = typeof k === "string" && /^-?\d+$/.test(k.trim()) ? parseInt(k, 10) : Number(k);
  if (!Number.isNaN(n)) {
    // Backend note: 2 or 3 means verified.
    if (n === 2 || n === 3) return "verified";
    if (n === 0 || n === 1) return "pending";
    if (n < 0) return "rejected";
    return null;
  }
  const str = String(k).toLowerCase();
  if (str.includes("reject") || str.includes("error") || str.includes("denied")) return "rejected";
  if (str.includes("pend") || str.includes("wait")) return "pending";
  if (str.includes("verif") || str.includes("approv") || str === "ok") return "verified";
  return null;
}

function parseClientRisk(client, info) {
  const r = firstDefined(
    client.RISK_TIER,
    info.RISK_TIER,
    client.RISK_LEVEL,
    info.RISK_LEVEL,
    client.RISK_STATUS,
    info.RISK_STATUS
  );
  if (isMissing(r)) return null;
  const str = String(r).toLowerCase();
  if (str.includes("high") || r === 2 || r === "2") return "high";
  if (str.includes("medium") || r === 1 || r === "1") return "medium";
  if (str.includes("low") || r === 0 || r === "0") return "low";
  return null;
}

function formatClientDate(raw) {
  if (isMissing(raw)) return NAN;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return NAN;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function normalizeIbClient(client, index) {
  const info = getUserInfo(client);
  const id = firstDefined(client.ID, client.USER_ID, client.UserId, client.id);
  const nameRaw = firstDefined(
    client.NICK_NAME,
    client.NICK,
    info.NICK_NAME,
    info.FULL_NAME,
    client.FULL_NAME,
    client.NAME,
    info.NAME,
    client.username
  );
  const emailRaw =
    firstDefined(
      info.EMAIL,
      info.USER_EMAIL,
      client.EMAIL,
      client.USER_EMAIL,
      client.email,
      client.user_email,
      client.MAIL,
      info.MAIL
    ) ?? extractEmailLoose(client);
  const phoneRaw = firstDefined(
    info.PHONE,
    info.PHONE_NUMBER,
    info.MOBILE,
    info.MOBILE_NUMBER,
    client.PHONE,
    client.PHONE_NUMBER,
    client.MOBILE,
    client.MOBILE_NUMBER,
    client.phone,
    client.phone_number,
    client.mobile,
    client.mobile_number,
    client.TEL,
    client.tel
  );
  const clientCodeRaw = firstDefined(
    client.CLIENT_CODE,
    client.USER_CODE,
    client.CODE,
    info.CLIENT_CODE
  );
  const statusKey = parseClientStatus(client, info);
  const kycKey = parseClientKyc(client, info);
  const riskKey = parseClientRisk(client, info);
  const dateRaw = firstDefined(
    client.CREATED_AT,
    client.DATE_CREATED,
    client.REGISTER_DATE,
    info.CREATED_AT,
    info.REGISTER_DATE,
    client.created_at
  );
  const photoRaw = firstDefined(
    info.PROFILE_PICTURE,
    info.PROFILE_IMAGE,
    info.PHOTO,
    client.PROFILE_PICTURE,
    client.AVATAR_URL
  );
  const rowKey =
    id != null && !isMissing(id) ? `id:${String(id)}` : `idx:${index}:${clientCodeRaw ?? ""}`;

  return {
    raw: client,
    rowKey,
    id,
    nameRaw,
    emailRaw,
    phoneRaw,
    clientCodeRaw,
    statusKey,
    kycKey,
    riskKey,
    dateRaw,
    photoRaw,
  };
}

function extractClientsArray(response) {
  let data = [];
  if (!response) return data;
  if (Array.isArray(response.DATA)) data = response.DATA;
  else if (response.DATA && Array.isArray(response.DATA.clients)) data = response.DATA.clients;
  else if (response.DATA && Array.isArray(response.DATA.data)) data = response.DATA.data;
  else if (Array.isArray(response)) data = response;
  else if (Array.isArray(response.clients)) data = response.clients;
  else if (Array.isArray(response.data)) data = response.data;
  else if (response.DATA && !Array.isArray(response.DATA)) data = [response.DATA];
  else if (!Array.isArray(response)) data = [response];
  return data;
}

const IbClientsList = () => {
  const { t } = useTranslation("mobile");
  const navigate = useNavigate();
  const isMobileLayout = useMediaMaxWidth(768);
  const [user, setUser] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filterStatus, setFilterStatus] = useState("all");
  const [filterKyc, setFilterKyc] = useState("all");
  const [filterRisk, setFilterRisk] = useState("all");
  const [filterClientName, setFilterClientName] = useState("");
  const [pageClients, setPageClients] = useState(1);
  const [showVolumeColumn, setShowVolumeColumn] = useState(false);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [volumeRange, setVolumeRange] = useState({
    dateFrom: null,
    dateTo: null,
    presetId: "this_month",
  });
  const [volumeByClientId, setVolumeByClientId] = useState({});
  const [volumeLoading, setVolumeLoading] = useState(false);
  const [volumeError, setVolumeError] = useState(null);
  const [volumeSort, setVolumeSort] = useState("none");
  const [showTotalsColumns, setShowTotalsColumns] = useState(false);
  const [totalsByClientId, setTotalsByClientId] = useState({});
  const [totalsLoading, setTotalsLoading] = useState(false);
  const [totalsError, setTotalsError] = useState(null);
  const renderIbSkeleton = (className = "") => (
    <span className={`ib-dashboard-skeleton ${className}`.trim()} aria-hidden />
  );

  const normalizedClients = useMemo(
    () => (Array.isArray(clients) ? clients.map((c, i) => normalizeIbClient(c, i)) : []),
    [clients]
  );

  const filteredClients = useMemo(() => {
    const normalizedNameQuery = String(filterClientName || "").toLowerCase().trim();
    const rows = normalizedClients.filter((row) => {
      if (normalizedNameQuery) {
        const candidateName = String(row.nameRaw || "").toLowerCase();
        if (!candidateName.includes(normalizedNameQuery)) return false;
      }
      if (filterStatus !== "all" && row.statusKey !== filterStatus) return false;
      if (filterKyc === "not_verified") {
        if (row.kycKey === "verified") return false;
      } else if (filterKyc !== "all" && row.kycKey !== filterKyc) {
        return false;
      }
      if (filterRisk !== "all" && row.riskKey !== filterRisk) return false;
      return true;
    });

    if (volumeSort === "none") return rows;

    const getRowVolume = (row) => {
      const clientId = row?.id;
      if (isMissing(clientId)) return 0;
      const value = Number(volumeByClientId[String(clientId)]);
      return Number.isFinite(value) ? value : 0;
    };

    const direction = volumeSort === "high_to_low" ? -1 : 1;
    return [...rows].sort((a, b) => {
      const diff = (getRowVolume(a) - getRowVolume(b)) * direction;
      if (diff !== 0) return diff;
      return String(a.rowKey).localeCompare(String(b.rowKey));
    });
  }, [
    normalizedClients,
    filterClientName,
    filterStatus,
    filterKyc,
    filterRisk,
    volumeSort,
    volumeByClientId,
  ]);

  const totalFiltered = filteredClients.length;
  const totalPagesClients = Math.max(1, Math.ceil(totalFiltered / PER_PAGE) || 1);

  useEffect(() => {
    setPageClients((p) => Math.min(Math.max(1, p), totalPagesClients));
  }, [totalPagesClients]);

  const paginatedClients = useMemo(() => {
    const start = (pageClients - 1) * PER_PAGE;
    return filteredClients.slice(start, start + PER_PAGE);
  }, [filteredClients, pageClients]);

  const showingFrom = totalFiltered === 0 ? 0 : (pageClients - 1) * PER_PAGE + 1;
  const showingTo = totalFiltered === 0 ? 0 : Math.min(pageClients * PER_PAGE, totalFiltered);

  const kpi = useMemo(() => {
    const total = normalizedClients.length;
    const active = normalizedClients.filter((r) => r.statusKey === "completed").length;
    const pendingKyc = normalizedClients.filter((r) => r.kycKey === "pending").length;
    return { total, active, pendingKyc };
  }, [normalizedClients]);

  const handleOpenWhatsApp = (rawPhone) => {
    if (!rawPhone) return;
    const cleaned = String(rawPhone).replace(/[^0-9+]/g, "").trim();
    if (!cleaned) return;
    const digits = cleaned.startsWith("+") ? cleaned.slice(1) : cleaned;
    if (!digits) return;
    const url = `https://wa.me/${digits}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleOpenEmail = (rawEmail) => {
    if (!rawEmail) return;
    const email = String(rawEmail).trim();
    if (!email || email === NAN) return;
    window.location.href = `mailto:${email}`;
  };

  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem("userData");
      if (storedUser) setUser(JSON.parse(storedUser));
    } catch (_) {}
  }, []);

  const { brokerPerm, brokerDevicePerm } = useMemo(
    () => getPermissionControllers(user),
    [user]
  );
  const canViewClientsList = useMemo(() => {
    const canViewClient = hasBrokerPermission(
      brokerPerm,
      brokerDevicePerm,
      PermissionBrokerModelController.PER__CAN_VIEW_CLIENT
    );
    return isBroker(user) && canViewClient;
  }, [user, brokerPerm, brokerDevicePerm]);
  const canViewTradingHistory = useMemo(
    () =>
      canViewClientsList &&
      !isAccountPermissionDisabled(
        brokerPerm,
        PermissionBrokerModelController.PER__CAN_VIEW_CLIENT_TRADING_HISTORY
      ),
    [canViewClientsList, brokerPerm]
  );
  const canViewBalanceEquityClients = useMemo(
    () =>
      canViewClientsList &&
      !isAccountPermissionDisabled(
        brokerPerm,
        PermissionBrokerModelController.PER__CAN_VIEW_BALANCE_EQUITY_CLIENTS
      ),
    [canViewClientsList, brokerPerm]
  );

  useEffect(() => {
    if (!user) return;
    if (!canViewClientsList) {
      navigate("/pages/ibDashboard/dashboard", { replace: true });
    }
  }, [user, canViewClientsList, navigate]);

  useEffect(() => {
    if (!user || !canViewClientsList) return;

    const loadClients = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetchAgentClients({
          page: 0,
          withDoc: 0,
          search: null,
        });
        const data = extractClientsArray(response);
        setClients(data);
      } catch (_) {
        setError("Failed to load IB clients");
        setClients([]);
      } finally {
        setLoading(false);
      }
    };

    loadClients();
  }, [user, canViewClientsList]);

  useEffect(() => {
    setPageClients(1);
  }, [filterClientName, filterStatus, filterKyc, filterRisk, volumeSort]);

  useEffect(() => {
    if (canViewTradingHistory) return;
    setShowDateFilter(false);
    setShowVolumeColumn(false);
    setVolumeByClientId({});
    setVolumeError(null);
    setVolumeLoading(false);
    setVolumeSort("none");
  }, [canViewTradingHistory]);

  useEffect(() => {
    if (canViewBalanceEquityClients) return;
    setShowTotalsColumns(false);
    setTotalsByClientId({});
    setTotalsError(null);
    setTotalsLoading(false);
  }, [canViewBalanceEquityClients]);

  const getNickName = (client) =>
    firstDefined(
      client.NICK_NAME,
      client.NICK,
      client.nick,
      client.nick_name,
      client.NICKNAME
    );

  const getUserId = (client) =>
    firstDefined(client.ID, client.USER_ID, client.UserId, client.user_id, client.id);

  const handleViewClientAccounts = (rawClient) => {
    const userId = getUserId(rawClient);
    if (isMissing(userId)) return;
    navigate(`/pages/ibDashboard/client-accounts/${userId}`, {
      state: { clientName: disp(getNickName(rawClient)) },
    });
  };

  const loadClientVolumes = async (dateFromValue, dateToValue) => {
    if (!canViewTradingHistory) return;
    if (!dateFromValue || !dateToValue) {
      setVolumeError(
        t("ib_trading_history_dates_required", {
          defaultValue: "Please choose a from and to date.",
        })
      );
      return;
    }

    setVolumeLoading(true);
    setVolumeError(null);
    try {
      const response = await fetchClientsTradingHistory({
        dateFrom: dateFromValue,
        dateTo: dateToValue,
      });
      const code = Number(response?.CODE);
      if (code === AppConstants.responseNeedPermission || code === 204) {
        setVolumeByClientId({});
        setShowVolumeColumn(false);
        setVolumeError(null);
        setVolumeSort("none");
        return;
      }
      if (code !== AppConstants.responseOk) {
        setVolumeByClientId({});
        setVolumeError(
          t("ib_trading_history_load_error", {
            defaultValue: "Could not load trading history.",
          })
        );
        return;
      }
      const volumeRows = Array.isArray(response?.DATA?.CLIENTS)
        ? response.DATA.CLIENTS
        : [];
      const nextMap = {};
      volumeRows.forEach((row) => {
        const clientId = firstDefined(row.CLIENT_ID, row.USER_ID, row.id);
        if (isMissing(clientId)) return;
        nextMap[String(clientId)] = Number(row.TOTAL_VOLUME) || 0;
      });
      setVolumeByClientId(nextMap);
    } catch (_) {
      setVolumeByClientId({});
      setVolumeError(
        t("ib_trading_history_load_error", {
          defaultValue: "Could not load trading history.",
        })
      );
    } finally {
      setVolumeLoading(false);
    }
  };

  const handleApplyVolumeRange = ({ dateFrom, dateTo, presetId }) => {
    if (!canViewTradingHistory) return;
    setVolumeRange({ dateFrom, dateTo, presetId });
    if (!dateFrom || !dateTo) {
      setShowVolumeColumn(false);
      setVolumeByClientId({});
      setVolumeError(null);
      setVolumeSort("none");
      return;
    }
    setShowVolumeColumn(true);
    setShowTotalsColumns(false);
    setTotalsError(null);
    loadClientVolumes(dateFrom, dateTo);
  };

  const clearFilters = () => {
    setFilterClientName("");
    setFilterStatus("all");
    setFilterKyc("all");
    setFilterRisk("all");
    setShowVolumeColumn(false);
    setVolumeByClientId({});
    setVolumeError(null);
    setVolumeSort("none");
    setShowTotalsColumns(false);
    setTotalsByClientId({});
    setTotalsError(null);
    setVolumeRange({
      dateFrom: null,
      dateTo: null,
      presetId: "this_month",
    });
  };

  const loadClientTotals = async () => {
    if (!canViewBalanceEquityClients) return;

    setTotalsLoading(true);
    setTotalsError(null);
    try {
      const response = await fetchClientsAccountTotals();
      const code = Number(response?.CODE);
      if (code === AppConstants.responseNeedPermission || code === 204) {
        setTotalsByClientId({});
        setShowTotalsColumns(false);
        setTotalsError(null);
        return;
      }
      if (code !== AppConstants.responseOk) {
        setTotalsByClientId({});
        setTotalsError(
          t("ib_account_totals_load_error", {
            defaultValue: "Could not load balance and equity.",
          })
        );
        return;
      }
      const rows = Array.isArray(response?.DATA?.CLIENTS) ? response.DATA.CLIENTS : [];
      const nextMap = {};
      rows.forEach((row) => {
        const clientId = firstDefined(row.CLIENT_ID, row.USER_ID, row.id);
        if (isMissing(clientId)) return;
        nextMap[String(clientId)] = {
          balance: Number(row.TOTAL_BALANCE ?? row.BALANCE) || 0,
          equity: Number(row.TOTAL_EQUITY ?? row.EQUITY) || 0,
        };
      });
      setTotalsByClientId(nextMap);
      setShowTotalsColumns(true);
    } catch (_) {
      setTotalsByClientId({});
      setTotalsError(
        t("ib_account_totals_load_error", {
          defaultValue: "Could not load balance and equity.",
        })
      );
    } finally {
      setTotalsLoading(false);
    }
  };

  const handleToggleAccountTotals = () => {
    if (!canViewBalanceEquityClients || totalsLoading) return;
    if (showTotalsColumns) {
      setShowTotalsColumns(false);
      setTotalsError(null);
      return;
    }
    setShowVolumeColumn(false);
    setVolumeError(null);
    setVolumeSort("none");
    if (Object.keys(totalsByClientId).length > 0) {
      setShowTotalsColumns(true);
      return;
    }
    loadClientTotals();
  };

  const allClientsAccountTotals = useMemo(() => {
    const summed = Object.values(totalsByClientId).reduce(
      (acc, row) => {
        acc.balance += Number(row?.balance) || 0;
        acc.equity += Number(row?.equity) || 0;
        return acc;
      },
      { balance: 0, equity: 0 }
    );
    return {
      balance: Math.round(summed.balance * 100) / 100,
      equity: Math.round(summed.equity * 100) / 100,
    };
  }, [totalsByClientId]);

  const tradingHistoryLabel = volumeLoading
    ? t("ib_trading_history_loading", { defaultValue: "Loading…" })
    : showVolumeColumn && volumeRange.dateFrom && volumeRange.dateTo
      ? `${formatClientDate(volumeRange.dateFrom)} – ${formatClientDate(volumeRange.dateTo)}`
      : t("ib_trading_history_select_dates", { defaultValue: "Select dates" });

  const statusClass = (statusKey) => {
    if (statusKey === "completed") return "ib-dashboard-status--active";
    if (statusKey === "incomplete") return "ib-dashboard-status--pending";
    if (statusKey === "active") return "ib-dashboard-status--active";
    if (statusKey === "pending") return "ib-dashboard-status--pending";
    if (statusKey === "suspended") return "ib-dashboard-status--suspended";
    return "ib-dashboard-status--inactive";
  };

  const statusLabel = (statusKey) => {
    if (statusKey === "completed") return t("completed", { defaultValue: "Completed" });
    if (statusKey === "incomplete") return t("incomplete", { defaultValue: "Incomplete" });
    if (statusKey === "active") return t("ib_status_active");
    if (statusKey === "pending") return t("ib_status_pending");
    if (statusKey === "suspended") return t("ib_status_suspended");
    if (statusKey === "inactive") return t("ib_status_inactive");
    return NAN;
  };

  const kycLabel = (kycKey) => {
    if (kycKey === "verified") return t("ib_kyc_verified");
    if (kycKey === "pending") return t("ib_kyc_pending");
    if (kycKey === "rejected") return t("ib_kyc_rejected");
    return NAN;
  };

  const kycIconClass = (kycKey) => {
    if (kycKey === "verified") return "fa-solid fa-circle-check";
    if (kycKey === "pending") return "fa-regular fa-clock";
    if (kycKey === "rejected") return "fa-solid fa-circle-xmark";
    return "fa-solid fa-minus";
  };

  const kycPillClass = (kycKey) => {
    if (kycKey === "verified") return "ib-clients-kyc--verified";
    if (kycKey === "pending") return "ib-clients-kyc--pending";
    if (kycKey === "rejected") return "ib-clients-kyc--rejected";
    return "ib-clients-kyc--unknown";
  };

  const riskLabel = (riskKey) => {
    if (riskKey === "low") return t("ib_risk_low");
    if (riskKey === "medium") return t("ib_risk_medium");
    if (riskKey === "high") return t("ib_risk_high");
    return NAN;
  };

  const riskTextClass = (riskKey) => {
    if (riskKey === "low") return "ib-clients-risk--low";
    if (riskKey === "medium") return "ib-clients-risk--medium";
    if (riskKey === "high") return "ib-clients-risk--high";
    return "ib-clients-risk--nan";
  };

  const columnsClients = [
    {
      key: "name",
      label: t("ib_col_client_name"),
      className: "ib-clients-col-name",
      minWidth: 140,
      render: (row) => (
        <div className="ib-dashboard-cell-user">
          {row.photoRaw && !isMissing(row.photoRaw) ? (
            <img
              src={row.photoRaw}
              alt=""
              className="ib-dashboard-avatar"
              style={{ objectFit: "cover", padding: 0 }}
            />
          ) : (
            <span className="ib-dashboard-avatar" aria-hidden>
              {avatarInitials(row.nameRaw)}
            </span>
          )}
          <div className="ib-dashboard-cell-user-text">
            <span className="ib-dashboard-name" title={disp(row.nameRaw)}>
              {disp(row.nameRaw)}
            </span>
            {isMobileLayout ? (
              <>
                <span
                  className="ib-clients-id-email-sub ib-clients-id-email-sub--email"
                  title={disp(row.emailRaw)}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpenEmail(row.emailRaw)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleOpenEmail(row.emailRaw);
                    }
                  }}
                >
                  {disp(row.emailRaw)}
                </span>
                <span
                  className="ib-clients-id-email-sub ib-clients-id-email-sub--phone"
                  title={disp(row.phoneRaw)}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpenWhatsApp(row.phoneRaw)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleOpenWhatsApp(row.phoneRaw);
                    }
                  }}
                >
                  {disp(row.phoneRaw)}
                </span>
              </>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      key: "idEmail",
      label: t("contact", { defaultValue: "Contact" }),
      className: "ib-clients-col-id-email",
      minWidth: 140,
      render: (row) => (
        <div>
          <div
            className="ib-clients-id-email-sub ib-clients-id-email-sub--email"
            role="button"
            tabIndex={0}
            onClick={() => handleOpenEmail(row.emailRaw)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleOpenEmail(row.emailRaw);
              }
            }}
          >
            {disp(row.emailRaw)}
          </div>
          <div
            className="ib-clients-id-email-sub ib-clients-id-email-sub--phone"
            role="button"
            tabIndex={0}
            onClick={() => handleOpenWhatsApp(row.phoneRaw)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleOpenWhatsApp(row.phoneRaw);
              }
            }}
          >
            {disp(row.phoneRaw)}
          </div>
        </div>
      ),
    },
    {
      key: "status",
      label: t("ib_col_status"),
      className: "ib-clients-col-status",
      minWidth: 88,
      render: (row) => (
        <span className={`ib-dashboard-status ${statusClass(row.statusKey)}`}>
          <span className="ib-dashboard-status-dot" aria-hidden />
          {statusLabel(row.statusKey)}
        </span>
      ),
    },
    {
      key: "kyc",
      label: t("ib_col_kyc"),
      className: "ib-clients-col-kyc",
      minWidth: 96,
      render: (row) => (
        <span className={`ib-clients-kyc ${kycPillClass(row.kycKey)}`}>
          <i className={kycIconClass(row.kycKey)} aria-hidden />
          {kycLabel(row.kycKey)}
        </span>
      ),
    },
    ...(canViewTradingHistory && showVolumeColumn
      ? [
          {
            key: "totalVolume",
            label: t("ib_trading_history_total_volume", { defaultValue: "Total volume" }),
            className: "ib-clients-col-volume",
            minWidth: 108,
            render: (row) => {
              if (volumeLoading) {
                return renderIbSkeleton("ib-dashboard-skeleton--kpi-value");
              }
              const clientId = row.id;
              const volume =
                isMissing(clientId) || !(String(clientId) in volumeByClientId)
                  ? 0
                  : volumeByClientId[String(clientId)];
              return (
                <span className="ib-clients-history-metric ib-clients-history-metric--volume">
                  {formatLots(volume)} {t("ib_trading_history_lots", { defaultValue: "lots" })}
                </span>
              );
            },
          },
        ]
      : []),
    ...(canViewBalanceEquityClients && showTotalsColumns
      ? [
          {
            key: "totalBalance",
            label: t("ib_trading_history_total_balance", { defaultValue: "Total balance" }),
            className: "ib-clients-col-balance",
            minWidth: 118,
            render: (row) => {
              if (totalsLoading) {
                return renderIbSkeleton("ib-dashboard-skeleton--kpi-value");
              }
              const metrics = totalsByClientId[String(row.id)] || {};
              return <span>{formatMoney(metrics.balance)}</span>;
            },
          },
          {
            key: "totalEquity",
            label: t("ib_trading_history_total_equity", { defaultValue: "Total equity" }),
            className: "ib-clients-col-equity",
            minWidth: 118,
            render: (row) => {
              if (totalsLoading) {
                return renderIbSkeleton("ib-dashboard-skeleton--kpi-value");
              }
              const metrics = totalsByClientId[String(row.id)] || {};
              return <span>{formatMoney(metrics.equity)}</span>;
            },
          },
        ]
      : []),
    {
      key: "risk",
      label: t("ib_col_risk_tier"),
      className: "ib-clients-col-risk",
      minWidth: 72,
      render: (row) => (
        <span className={`ib-clients-risk-meter ${riskTextClass(row.riskKey)}`}>
          <span
            className="ib-clients-risk-meter-track"
            title={riskLabel(row.riskKey)}
            aria-label={riskLabel(row.riskKey)}
          >
            <span className="ib-clients-risk-meter-fill" />
          </span>
        </span>
      ),
    },
    {
      key: "dateAdded",
      label: t("ib_col_date_added"),
      className: "ib-clients-col-date",
      minWidth: 96,
      render: (row) => <span>{formatClientDate(row.dateRaw)}</span>,
    },
    {
      key: "actions",
      label: t("ib_col_action"),
      className: "action-column",
      minWidth: 56,
      render: (row) => (
        <button
          type="button"
          className="request-action-btn request-action-btn--view"
          onClick={() => handleViewClientAccounts(row.raw)}
          aria-label={t("ib_clients_view_accounts_aria")}
        >
          <i className="fa-solid fa-eye" aria-hidden />
        </button>
      ),
    },
  ];

  const paginationSummary =
    !loading && !error ? (
      <p className="ib-clients-pagination-summary">
        {t("ib_pagination_showing", { from: showingFrom, to: showingTo, total: totalFiltered })}
      </p>
    ) : null;

  return (
    <div className="ib-dashboard-page ib-clients-list-page">
      <Sidebar user={user} />
      <div className="ib-dashboard-main">
        <div className="container-fluid px-0 w-100">
            <h1 className="ib-clients-list-page-title">{t("ib_clients_title")}</h1>

            <div className="ib-dashboard-kpi-grid ib-dashboard-kpi-grid--clients">
              <article className="ib-dashboard-kpi-card">
                <div className="ib-dashboard-kpi-top">
                  <div className="ib-dashboard-kpi-icon-wrap">
                    <i className="fa-solid fa-users" />
                  </div>
                </div>
                <p className="ib-dashboard-kpi-label">{t("ib_kpi_direct_clients")}</p>
                <p className="ib-dashboard-kpi-value">
                  {!loading ? kpi.total.toLocaleString("en-US") : renderIbSkeleton("ib-dashboard-skeleton--kpi-value")}
                </p>
              </article>

              <article className="ib-dashboard-kpi-card">
                <div className="ib-dashboard-kpi-top">
                  <div className="ib-dashboard-kpi-icon-wrap">
                    <i className="fa-solid fa-user-check" />
                  </div>
                </div>
                <p className="ib-dashboard-kpi-label">{t("ib_kpi_active_clients")}</p>
                <p className="ib-dashboard-kpi-sub">{t("ib_this_month")}</p>
                <p className="ib-dashboard-kpi-value">
                  {!loading ? kpi.active.toLocaleString("en-US") : renderIbSkeleton("ib-dashboard-skeleton--kpi-value")}
                </p>
              </article>

              <article
                className="ib-dashboard-kpi-card ib-dashboard-kpi-card--highlight"
                role="button"
                tabIndex={0}
                onClick={() =>
                  setFilterKyc((prev) => (prev === "not_verified" ? "all" : "not_verified"))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setFilterKyc((prev) => (prev === "not_verified" ? "all" : "not_verified"));
                  }
                }}
                aria-pressed={filterKyc === "not_verified"}
                style={{ cursor: "pointer" }}
              >
                <div className="ib-dashboard-kpi-top">
                  <div className="ib-dashboard-kpi-icon-wrap">
                    <i className="fa-solid fa-user-clock" />
                  </div>
                  <span className="ib-dashboard-kpi-alert">
                    {filterKyc === "not_verified"
                      ? t("ib_filter_active", { defaultValue: "Filter Active" })
                      : t("ib_kpi_action_needed", { defaultValue: "Action Needed" })}
                  </span>
                </div>
                <p className="ib-dashboard-kpi-label">
                  {t("ib_kpi_non_verified_kyc", { defaultValue: "Pending KYC" })}
                </p>
                <p className="ib-dashboard-kpi-value">
                  {!loading
                    ? normalizedClients
                        .filter((r) => r.kycKey !== "verified")
                        .length
                        .toLocaleString("en-US")
                    : renderIbSkeleton("ib-dashboard-skeleton--kpi-value")}
                </p>
              </article>
            </div>

            <div className="ib-clients-filters-toolbar w-100">
              <div className="ib-clients-filters-left">
                <div className="ib-clients-filter-field ib-clients-filter-field--search">
                  <label htmlFor="ib-filter-client-name">{t("ib_col_client_name")}</label>
                  <input
                    id="ib-filter-client-name"
                    type="text"
                    className="ib-clients-tree-search-input"
                    placeholder={t("ib_tree_search_placeholder")}
                    value={filterClientName}
                    onChange={(e) => setFilterClientName(e.target.value)}
                  />
                </div>
                <div className="ib-clients-filter-field">
                  <label htmlFor="ib-filter-status">{t("ib_filter_status")}</label>
                  <select
                    id="ib-filter-status"
                    className="ib-clients-filter-select"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="all">{t("ib_filter_all")}</option>
                    <option value="completed">{t("completed", { defaultValue: "Completed" })}</option>
                    <option value="incomplete">{t("incomplete", { defaultValue: "Incomplete" })}</option>
                  </select>
                </div>
                <div className="ib-clients-filter-field">
                  <label htmlFor="ib-filter-kyc">{t("ib_filter_kyc")}</label>
                  <select
                    id="ib-filter-kyc"
                    className="ib-clients-filter-select"
                    value={filterKyc}
                    onChange={(e) => setFilterKyc(e.target.value)}
                  >
                    <option value="all">{t("ib_filter_all")}</option>
                    <option value="verified">{t("ib_kyc_verified")}</option>
                    <option value="not_verified">
                      {t("ib_kyc_not_verified", { defaultValue: "Not Verified" })}
                    </option>
                    <option value="pending">{t("ib_kyc_pending")}</option>
                    <option value="rejected">{t("ib_kyc_rejected")}</option>
                  </select>
                </div>
                <div className="ib-clients-filter-field">
                  <label htmlFor="ib-filter-risk">{t("ib_filter_risk")}</label>
                  <select
                    id="ib-filter-risk"
                    className="ib-clients-filter-select"
                    value={filterRisk}
                    onChange={(e) => setFilterRisk(e.target.value)}
                  >
                    <option value="all">{t("ib_filter_all")}</option>
                    <option value="low">{t("ib_risk_low")}</option>
                    <option value="medium">{t("ib_risk_medium")}</option>
                    <option value="high">{t("ib_risk_high")}</option>
                  </select>
                </div>
                {(canViewTradingHistory || canViewBalanceEquityClients) &&
                normalizedClients.length > 0 ? (
                  <div className="ib-clients-trading-history-controls">
                    {canViewTradingHistory ? (
                      <div className="ib-clients-filter-field ib-clients-filter-field--trading-history">
                        <label htmlFor="ib-filter-trading-history">
                          {t("ib_trading_history", { defaultValue: "Trading History" })}
                        </label>
                        <button
                          id="ib-filter-trading-history"
                          type="button"
                          className={`ib-clients-filter-date${
                            showVolumeColumn ? " ib-clients-filter-date--active" : ""
                          }`}
                          onClick={() => setShowDateFilter(true)}
                          disabled={volumeLoading}
                        >
                          <i className="fa-solid fa-calendar-days" aria-hidden />
                          <span>{tradingHistoryLabel}</span>
                        </button>
                      </div>
                    ) : null}
                    {canViewTradingHistory && showVolumeColumn ? (
                      <div className="ib-clients-filter-field ib-clients-filter-field--volume-sort">
                        <label htmlFor="ib-filter-volume-sort">
                          {t("ib_filter_volume", { defaultValue: "Volume" })}
                        </label>
                        <select
                          id="ib-filter-volume-sort"
                          className="ib-clients-filter-select"
                          value={volumeSort}
                          onChange={(e) => setVolumeSort(e.target.value)}
                          disabled={volumeLoading}
                        >
                          <option value="none">
                            {t("ib_filter_volume_default", { defaultValue: "Default" })}
                          </option>
                          <option value="high_to_low">
                            {t("ib_filter_volume_high_to_low", { defaultValue: "High to low" })}
                          </option>
                          <option value="low_to_high">
                            {t("ib_filter_volume_low_to_high", { defaultValue: "Low to high" })}
                          </option>
                        </select>
                      </div>
                    ) : null}
                    {canViewBalanceEquityClients ? (
                      <div className="ib-clients-filter-field ib-clients-filter-field--account-totals">
                        <label htmlFor="ib-filter-account-totals">
                          {t("ib_account_totals", { defaultValue: "Balance & Equity" })}
                        </label>
                        <button
                          id="ib-filter-account-totals"
                          type="button"
                          className={`ib-clients-filter-date${
                            showTotalsColumns ? " ib-clients-filter-date--active" : ""
                          }`}
                          onClick={handleToggleAccountTotals}
                          disabled={totalsLoading}
                        >
                          <i className="fa-solid fa-scale-balanced" aria-hidden />
                          <span>
                            {totalsLoading
                              ? t("ib_account_totals_loading", { defaultValue: "Loading…" })
                              : showTotalsColumns
                                ? t("ib_account_totals_hide", { defaultValue: "Hide totals" })
                                : t("ib_account_totals_get", { defaultValue: "Get totals" })}
                          </span>
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <button type="button" className="ib-clients-filter-clear" onClick={clearFilters}>
                  <i className="fa-solid fa-filter-circle-xmark" aria-hidden />
                  {t("ib_filter_clear")}
                </button>
              </div>
              <div className="ib-clients-bulk" aria-label={t("ib_bulk_actions")}>
                <span>{t("ib_bulk_actions")}</span>
                <button type="button" className="ib-clients-bulk-btn" aria-label="Email">
                  <i className="fa-regular fa-envelope" />
                </button>
                <button type="button" className="ib-clients-bulk-btn" aria-label="Download">
                  <i className="fa-solid fa-download" />
                </button>
              </div>
            </div>

            {canViewBalanceEquityClients && (showTotalsColumns || totalsLoading) ? (
              <div
                className="ib-clients-totals-box"
                role="region"
                aria-label={t("ib_account_totals", { defaultValue: "Balance & Equity" })}
                aria-live="polite"
              >
                <p className="ib-clients-totals-box-hint">
                  {t("ib_account_totals_sum_hint", { defaultValue: "Sum of all clients" })}
                </p>
                <div className="ib-clients-totals-box-grid">
                  <div className="ib-clients-totals-box-item">
                    <span>
                      <i className="fa-solid fa-wallet" aria-hidden />
                      {t("ib_trading_history_total_balance", { defaultValue: "Total balance" })}
                    </span>
                    <strong>
                      {totalsLoading
                        ? renderIbSkeleton("ib-dashboard-skeleton--kpi-value")
                        : formatMoney(allClientsAccountTotals.balance)}
                    </strong>
                  </div>
                  <div className="ib-clients-totals-box-item">
                    <span>
                      <i className="fa-solid fa-scale-balanced" aria-hidden />
                      {t("ib_trading_history_total_equity", { defaultValue: "Total equity" })}
                    </span>
                    <strong>
                      {totalsLoading
                        ? renderIbSkeleton("ib-dashboard-skeleton--kpi-value")
                        : formatMoney(allClientsAccountTotals.equity)}
                    </strong>
                  </div>
                </div>
              </div>
            ) : null}

            <DataTable
              title=""
              columns={columnsClients}
              data={paginatedClients}
              rowKey={(row) => row.rowKey}
              currentPage={pageClients}
              totalItems={totalFiltered}
              perPage={PER_PAGE}
              onPageChange={setPageClients}
              hideTopToolbar
              loading={loading}
              loadingMessage={t("ib_clients_loading")}
              error={error}
              noDataMessage={t("ib_clients_no_results")}
              containerClassName="container-fluid px-0 w-100 ib-clients-list-container"
              tableClassName="ib-clients-full-table"
              paginationLeading={paginationSummary}
              compactPagination
              resizableColumns
              resizableStorageKey="ib-clients-list"
            />
            {canViewTradingHistory && showVolumeColumn && volumeError ? (
              <p className="ib-clients-volume-error">{volumeError}</p>
            ) : null}
            {canViewBalanceEquityClients && totalsError ? (
              <p className="ib-clients-volume-error">{totalsError}</p>
            ) : null}
        </div>
      </div>
      {canViewTradingHistory ? (
      <DateRangeFilter
        isOpen={showDateFilter}
        onClose={() => setShowDateFilter(false)}
        onApply={handleApplyVolumeRange}
        initialRange={volumeRange}
      />
      ) : null}
    </div>
  );
};

export default IbClientsList;

